if (typeof ReadMoreDescription !== 'function') {
  class ReadMoreDescription extends HTMLElement {
    constructor() {
      super();
      this.contentEl = this.querySelector('[data-js-read-more-content]');
      this.buttonEl = this.querySelector('[data-js-read-more-button]');
      this.buttonTextEl = this.querySelector('[data-js-read-more-text]');
      this.maxLines = parseInt(this.getAttribute('data-max-lines') || '6', 10);
      this.readMoreText = this.getAttribute('data-read-more') || 'Read More';
      this.readLessText = this.getAttribute('data-read-less') || 'Read Less';
      this.isCollapsed = true;
      this.transitioning = false;

      if (!this.contentEl || !this.buttonEl) return;

      this.init();
    }

    connectedCallback() {
      if (!this.contentEl || !this.buttonEl) return;

      // Handle ResizeObserver for responsive line wrapping
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          if (!this.transitioning) {
            this.checkHeight();
          }
        });
        this.resizeObserver.observe(this.contentEl);
      } else {
        window.addEventListener('resize', this.debounce(() => {
          if (!this.transitioning) {
            this.checkHeight();
          }
        }, 150));
      }
    }

    disconnectedCallback() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    }

    init() {
      this.buttonEl.addEventListener('click', () => this.toggleExpand());
      
      // Delay initial height check to ensure custom fonts and DOM styles are computed
      requestAnimationFrame(() => {
        setTimeout(() => this.checkHeight(), 50);
      });
    }

    getLineHeight() {
      const computedStyle = window.getComputedStyle(this.contentEl);
      let lineHeight = parseFloat(computedStyle.lineHeight);
      
      if (isNaN(lineHeight)) {
        const fontSize = parseFloat(computedStyle.fontSize) || 16;
        lineHeight = fontSize * 1.5;
      }
      
      return lineHeight;
    }

    calculateTargetHeight() {
      const lineHeight = this.getLineHeight();
      return Math.round(lineHeight * this.maxLines);
    }

    checkHeight() {
      if (this.transitioning) return;

      const targetHeight = this.calculateTargetHeight();
      const actualHeight = this.contentEl.scrollHeight;
      
      // Buffer of 8px to prevent toggling for tiny rounding errors
      const needsToggle = actualHeight > targetHeight + 8;

      if (!needsToggle) {
        this.buttonEl.setAttribute('hidden', '');
        this.contentEl.style.maxHeight = 'none';
        this.removeAttribute('data-collapsed');
        this.buttonEl.setAttribute('aria-expanded', 'true');
        return;
      }

      this.buttonEl.removeAttribute('hidden');

      if (this.isCollapsed) {
        this.contentEl.style.maxHeight = `${targetHeight}px`;
        this.setAttribute('data-collapsed', 'true');
        this.buttonEl.setAttribute('aria-expanded', 'false');
        if (this.buttonTextEl) {
          this.buttonTextEl.textContent = this.readMoreText;
        }
      } else {
        this.setAttribute('data-collapsed', 'false');
        this.buttonEl.setAttribute('aria-expanded', 'true');
        if (this.buttonTextEl) {
          this.buttonTextEl.textContent = this.readLessText;
        }
      }
    }

    toggleExpand() {
      if (this.transitioning) return;

      const targetHeight = this.calculateTargetHeight();
      const fullHeight = this.contentEl.scrollHeight;

      this.transitioning = true;

      if (this.isCollapsed) {
        // EXPANDING
        this.isCollapsed = false;
        this.setAttribute('data-collapsed', 'false');
        this.buttonEl.setAttribute('aria-expanded', 'true');
        
        if (this.buttonTextEl) {
          this.buttonTextEl.textContent = this.readLessText;
        }

        this.contentEl.style.maxHeight = `${fullHeight}px`;

        const onTransitionEnd = (e) => {
          if (e.target !== this.contentEl) return;
          this.contentEl.removeEventListener('transitionend', onTransitionEnd);
          if (!this.isCollapsed) {
            this.contentEl.style.maxHeight = 'none';
          }
          this.transitioning = false;
        };

        this.contentEl.addEventListener('transitionend', onTransitionEnd);

      } else {
        // COLLAPSING
        this.isCollapsed = true;
        
        // Lock to current pixel height first for smooth CSS transition
        this.contentEl.style.maxHeight = `${this.contentEl.getBoundingClientRect().height}px`;
        
        // Force reflow
        this.contentEl.offsetHeight;

        this.setAttribute('data-collapsed', 'true');
        this.buttonEl.setAttribute('aria-expanded', 'false');

        if (this.buttonTextEl) {
          this.buttonTextEl.textContent = this.readMoreText;
        }

        requestAnimationFrame(() => {
          this.contentEl.style.maxHeight = `${targetHeight}px`;
        });

        const onTransitionEnd = (e) => {
          if (e.target !== this.contentEl) return;
          this.contentEl.removeEventListener('transitionend', onTransitionEnd);
          this.transitioning = false;
        };

        this.contentEl.addEventListener('transitionend', onTransitionEnd);
      }
    }

    debounce(fn, wait) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    }
  }

  if (typeof customElements.get('read-more-description') === 'undefined') {
    customElements.define('read-more-description', ReadMoreDescription);
  }
}
